import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Copy, Check, DollarSign, Users, Power, Edit, ExternalLink, Sparkles } from 'lucide-react';

const DEFAULT_PLANS = [
  { id: 'basic', name: 'Básico' },
  { id: 'professional', name: 'Profissional' },
  { id: 'enterprise', name: 'Enterprise' },
];

const emptyForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  commission_by_plan: { basic: 10, professional: 15, enterprise: 20 },
};

const GerenciarParceirosPage = () => {
  const [partners, setPartners] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [savingTenantId, setSavingTenantId] = useState(null);
  const [formError, setFormError] = useState('');

  // Promover cliente a parceiro
  const [promoteTenant, setPromoteTenant] = useState(null);
  const [promoteForm, setPromoteForm] = useState({ partner_email: '', password: '', phone: '', link_tenant_to_new_partner: true });
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pRes, wRes, plansRes, tRes] = await Promise.all([
        api.get('/admin/partners'),
        api.get('/admin/withdrawals'),
        api.get('/saas/plans').catch(() => ({ data: { plans: DEFAULT_PLANS } })),
        api.get('/admin/tenants-with-partners').catch(() => ({ data: [] })),
      ]);
      setPartners(pRes.data || []);
      setWithdrawals(wRes.data || []);
      setTenants(tRes.data || []);
      const dbPlans = plansRes.data?.plans || [];
      if (dbPlans.length > 0) {
        setPlans(dbPlans.map((p) => ({ id: p.id, name: p.name })));
      }
    } catch (e) {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openCreate = () => {
    setEditingPartner(null);
    setFormError('');
    // Popular commission_by_plan com todos os planos
    const cbp = {};
    plans.forEach((p) => { cbp[p.id] = 10; });
    setForm({ ...emptyForm, commission_by_plan: cbp });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingPartner(p);
    setFormError('');
    setForm({
      name: p.name || '',
      email: p.email || '',
      password: '',
      phone: p.phone || '',
      commission_by_plan: p.commission_by_plan || {},
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setFormError('');
    if (!form.name || !form.email) {
      setFormError('Nome e email são obrigatórios');
      return;
    }
    // Validação básica de email
    if (!editingPartner && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setFormError('Email inválido — verifique o formato (ex.: nome@dominio.com)');
      return;
    }
    if (!editingPartner && !form.password) {
      setFormError('Defina uma senha para o novo parceiro');
      return;
    }
    if (!editingPartner && form.password.length < 4) {
      setFormError('A senha deve ter pelo menos 4 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editingPartner) {
        // PATCH — não permite mudar email/senha por aqui
        await api.patch(`/admin/partners/${editingPartner.id}`, {
          name: form.name,
          phone: form.phone,
          commission_by_plan: form.commission_by_plan,
        });
        toast.success('Parceiro atualizado');
      } else {
        await api.post('/admin/partners', {
          name: form.name,
          email: form.email.trim().toLowerCase(),
          password: form.password,
          phone: form.phone,
          commission_by_plan: form.commission_by_plan,
        });
        toast.success('Parceiro criado com sucesso!');
      }
      setShowModal(false);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erro ao salvar parceiro';
      setFormError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/partners/${p.id}`, { active: !p.active });
      toast.success(p.active ? 'Parceiro desativado' : 'Parceiro ativado');
      loadAll();
    } catch (e) {
      toast.error('Erro ao alterar status');
    }
  };

  const copyRefLink = (p) => {
    const link = `https://nfcesys.com.br/?ref=${p.ref_code}`;
    navigator.clipboard.writeText(link);
    setCopiedId(p.id);
    toast.success('Link copiado!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const processWithdrawal = async (w, status) => {
    const note = status === 'rejected' ? window.prompt('Motivo da rejeição (opcional):') : window.prompt('Comprovante/observação (opcional):');
    try {
      await api.post(`/admin/withdrawals/${w.id}/action`, {
        status,
        admin_note: note || '',
      });
      toast.success(status === 'paid' ? 'Saque marcado como PAGO' : 'Saque REJEITADO (valor devolvido ao parceiro)');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao processar saque');
    }
  };

  const reassignTenant = async (tenantId, newPartnerId) => {
    setSavingTenantId(tenantId);
    try {
      await api.patch(`/admin/tenants/${tenantId}/partner`, {
        partner_id: newPartnerId || null,
      });
      toast.success(newPartnerId ? 'Cliente vinculado ao parceiro' : 'Vínculo removido');
      // Atualização otimista
      setTenants((prev) => prev.map((t) => t.id === tenantId
        ? { ...t, referred_by_partner_id: newPartnerId || null, partner: newPartnerId ? partners.find((p) => p.id === newPartnerId) : null }
        : t));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao vincular');
      loadAll();
    } finally {
      setSavingTenantId(null);
    }
  };

  const openPromote = (tenant) => {
    setPromoteTenant(tenant);
    setPromoteError('');
    const cbp = {};
    plans.forEach((p) => { cbp[p.id] = 10; });
    setPromoteForm({
      partner_email: tenant.owner_email || '',
      password: '',
      phone: '',
      commission_by_plan: cbp,
      link_tenant_to_new_partner: true,
    });
  };

  const confirmPromote = async () => {
    setPromoteError('');
    if (!promoteForm.password || promoteForm.password.length < 4) {
      setPromoteError('Defina uma senha de acesso para o novo parceiro (mín. 4 caracteres)');
      return;
    }
    setPromoting(true);
    try {
      const r = await api.post(`/admin/tenants/${promoteTenant.id}/promote-to-partner`, {
        partner_email: (promoteForm.partner_email || '').trim().toLowerCase() || null,
        password: promoteForm.password,
        phone: promoteForm.phone || null,
        commission_by_plan: promoteForm.commission_by_plan,
        link_tenant_to_new_partner: promoteForm.link_tenant_to_new_partner,
      });
      toast.success(`Cliente ${promoteTenant.company_name} agora é parceiro! Código: ${r.data?.partner?.ref_code}`);
      setPromoteTenant(null);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erro ao promover a parceiro';
      setPromoteError(detail);
      toast.error(detail);
    } finally {
      setPromoting(false);
    }
  };

  if (loading) return <div className="p-8">Carregando parceiros...</div>;

  const pendingWithdrawals = withdrawals.filter((w) => w.status === 'pending');

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="gerenciar-parceiros-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Parceiros / Afiliados</h1>
          <p className="text-slate-600 mt-1">Gerencie parceiros, comissões e saques do programa de indicação</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-novo-parceiro">
          <UserPlus className="w-4 h-4 mr-2" />
          Novo Parceiro
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> Total de parceiros</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-blue-600">{partners.length}</div><p className="text-xs text-slate-500 mt-1">{partners.filter((p) => p.active).length} ativos</p></CardContent>
        </Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-500" /> Saldo total devido</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-emerald-600">{formatCurrency(partners.reduce((s, p) => s + (p.current_balance || 0), 0))}</div><p className="text-xs text-slate-500 mt-1">Total ganho pelos parceiros: {formatCurrency(partners.reduce((s, p) => s + (p.total_earned || 0), 0))}</p></CardContent>
        </Card>
        <Card className={pendingWithdrawals.length > 0 ? 'border-amber-400 bg-amber-50' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-500" /> Saques pendentes</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-amber-600">{pendingWithdrawals.length}</div>
            <p className="text-xs text-slate-500 mt-1">Total: {formatCurrency(pendingWithdrawals.reduce((s, w) => s + (w.valor || 0), 0))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Saques pendentes */}
      {pendingWithdrawals.length > 0 && (
        <Card className="mb-6 border-amber-300">
          <CardHeader><CardTitle>Saques pendentes ({pendingWithdrawals.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-withdrawals-table">
              <thead className="bg-amber-50 border-b"><tr>
                <th className="text-left p-3">Parceiro</th><th className="text-left p-3">Email</th>
                <th className="text-right p-3">Valor</th><th className="text-left p-3">PIX</th>
                <th className="text-left p-3">Solicitado em</th><th className="text-center p-3">Ações</th>
              </tr></thead>
              <tbody>
                {pendingWithdrawals.map((w) => (
                  <tr key={w.id} className="border-b" data-testid={`withdrawal-${w.id}`}>
                    <td className="p-3 font-medium">{w.partner_name}</td>
                    <td className="p-3 text-slate-600">{w.partner_email}</td>
                    <td className="p-3 text-right font-bold text-emerald-700">{formatCurrency(w.valor)}</td>
                    <td className="p-3 text-slate-600 text-xs break-all">{w.pix_key || '-'}</td>
                    <td className="p-3 text-slate-600">{new Date(w.requested_at).toLocaleString('pt-BR')}</td>
                    <td className="p-3 text-center space-x-2">
                      <Button size="sm" onClick={() => processWithdrawal(w, 'paid')} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`btn-pagar-${w.id}`}>Marcar Pago</Button>
                      <Button size="sm" variant="outline" onClick={() => processWithdrawal(w, 'rejected')} data-testid={`btn-rejeitar-${w.id}`}>Rejeitar</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Lista de parceiros */}
      <Card>
        <CardHeader><CardTitle>Todos os parceiros ({partners.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="partners-table">
            <thead className="bg-slate-50 border-b"><tr>
              <th className="text-left p-3">Nome</th><th className="text-left p-3">Email</th>
              <th className="text-left p-3">Código</th><th className="text-right p-3">Saldo</th>
              <th className="text-right p-3">Total Ganho</th><th className="text-center p-3">Status</th>
              <th className="text-center p-3">Ações</th>
            </tr></thead>
            <tbody>
              {partners.length === 0 ? (
                <tr><td colSpan="7" className="p-6 text-center text-slate-500">Nenhum parceiro cadastrado. Clique em &quot;Novo Parceiro&quot; para começar.</td></tr>
              ) : partners.map((p) => (
                <tr key={p.id} className="border-b" data-testid={`partner-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-slate-600">{p.email}</td>
                  <td className="p-3">
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{p.ref_code}</code>
                    <button onClick={() => copyRefLink(p)} className="ml-2 text-blue-600 hover:text-blue-800" title="Copiar link" data-testid={`btn-copy-${p.id}`}>
                      {copiedId === p.id ? <Check className="w-4 h-4 inline" /> : <Copy className="w-4 h-4 inline" />}
                    </button>
                  </td>
                  <td className="p-3 text-right font-semibold text-emerald-600">{formatCurrency(p.current_balance || 0)}</td>
                  <td className="p-3 text-right text-slate-700">{formatCurrency(p.total_earned || 0)}</td>
                  <td className="p-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded ${p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {p.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="p-3 text-center space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}><Edit className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(p)} data-testid={`btn-toggle-${p.id}`}><Power className="w-3 h-3" /></Button>
                    <a href={`https://nfcesys.com.br/?ref=${p.ref_code}`} target="_blank" rel="noopener noreferrer" className="inline-block">
                      <Button size="sm" variant="outline"><ExternalLink className="w-3 h-3" /></Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Clientes vinculados */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>Clientes (tenants) e seus parceiros ({tenants.length})</CardTitle>
            <Input
              placeholder="Buscar por empresa ou email..."
              className="max-w-sm"
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              data-testid="input-tenant-search"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Atribua ou remova o parceiro responsável por cada cliente. Comissões passadas não são afetadas.
          </p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tenants-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3">Empresa</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Plano</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Parceiro atual</th>
                <th className="text-left p-3">Reatribuir</th>
                <th className="text-left p-3">Tornar Parceiro</th>
              </tr>
            </thead>
            <tbody>
              {tenants
                .filter((t) => {
                  if (!tenantSearch) return true;
                  const q = tenantSearch.toLowerCase();
                  return (t.company_name || '').toLowerCase().includes(q)
                    || (t.owner_email || '').toLowerCase().includes(q);
                })
                .map((t) => {
                const isPartner = partners.some((p) => p.email && t.owner_email && p.email.toLowerCase() === t.owner_email.toLowerCase());
                return (
                <tr key={t.id} className="border-b" data-testid={`tenant-row-${t.id}`}>
                  <td className="p-3 font-medium">{t.company_name || '-'}</td>
                  <td className="p-3 text-slate-600">{t.owner_email || '-'}</td>
                  <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-1 rounded">{t.plan_id || '-'}</span></td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      t.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      t.subscription_status === 'trial' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{t.subscription_status || '-'}</span>
                  </td>
                  <td className="p-3 text-slate-700">
                    {t.partner ? (
                      <span className="text-sm">
                        <strong>{t.partner.name}</strong>
                        <code className="ml-2 text-xs bg-slate-100 px-1 rounded">{t.partner.ref_code}</code>
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">sem parceiro</span>
                    )}
                  </td>
                  <td className="p-3">
                    <select
                      className="w-full max-w-xs border border-slate-300 rounded px-2 py-1 text-sm"
                      value={t.referred_by_partner_id || ''}
                      disabled={savingTenantId === t.id}
                      onChange={(e) => reassignTenant(t.id, e.target.value)}
                      data-testid={`select-partner-${t.id}`}
                    >
                      <option value="">— Nenhum —</option>
                      {partners.filter((p) => p.active).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.ref_code})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3">
                    {isPartner ? (
                      <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded" data-testid={`already-partner-${t.id}`}>
                        ✓ já é parceiro
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPromote(t)}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`btn-promote-${t.id}`}
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        Tornar Parceiro
                      </Button>
                    )}
                  </td>
                </tr>
              );})}
              {tenants.length === 0 && (
                <tr><td colSpan="7" className="p-6 text-center text-slate-500">Nenhum cliente cadastrado ainda.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal Tornar Parceiro (promover cliente) */}
      <Dialog open={!!promoteTenant} onOpenChange={(open) => !open && setPromoteTenant(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promote-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Tornar cliente em parceiro
            </DialogTitle>
            <DialogDescription>
              O cliente <strong>{promoteTenant?.company_name}</strong> vai ganhar um cadastro de parceiro
              com código próprio de indicação, minisite público e comissão sobre novos clientes que ele trouxer.
            </DialogDescription>
          </DialogHeader>
          {promoteTenant && (
            <div className="space-y-4">
              {promoteError && (
                <div className="p-3 bg-rose-50 border border-rose-300 rounded-lg text-sm text-rose-800" data-testid="promote-error">
                  ⚠ {promoteError}
                </div>
              )}
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                Empresa: <strong>{promoteTenant.company_name}</strong><br />
                Email do dono: <strong>{promoteTenant.owner_email || '(sem email)'}</strong>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email de login como parceiro *</Label>
                  <Input
                    type="email"
                    value={promoteForm.partner_email}
                    onChange={(e) => setPromoteForm({ ...promoteForm, partner_email: e.target.value })}
                    placeholder="Use o email do dono ou outro"
                    data-testid="promote-email"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Deixe o email do dono ou troque por outro (se o dono já é usuário do sistema, use um email diferente).
                  </p>
                </div>
                <div>
                  <Label>Senha inicial *</Label>
                  <Input
                    type="text"
                    value={promoteForm.password}
                    onChange={(e) => setPromoteForm({ ...promoteForm, password: e.target.value })}
                    placeholder="Mín. 4 caracteres"
                    data-testid="promote-password"
                  />
                </div>
              </div>

              <div>
                <Label>Telefone (opcional)</Label>
                <Input
                  value={promoteForm.phone}
                  onChange={(e) => setPromoteForm({ ...promoteForm, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  data-testid="promote-phone"
                />
              </div>

              <div>
                <Label className="mb-2 block">% de comissão por plano</Label>
                <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                  {plans.map((plan) => (
                    <div key={plan.id} className="flex items-center gap-3">
                      <span className="flex-1 text-sm font-medium">{plan.name}</span>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        className="w-24"
                        value={promoteForm.commission_by_plan?.[plan.id] ?? 0}
                        onChange={(e) => setPromoteForm({
                          ...promoteForm,
                          commission_by_plan: {
                            ...(promoteForm.commission_by_plan || {}),
                            [plan.id]: parseFloat(e.target.value) || 0,
                          },
                        })}
                        data-testid={`promote-commission-${plan.id}`}
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="link-tenant-check"
                  className="mt-1"
                  checked={promoteForm.link_tenant_to_new_partner}
                  onChange={(e) => setPromoteForm({ ...promoteForm, link_tenant_to_new_partner: e.target.checked })}
                  data-testid="promote-link-checkbox"
                />
                <label htmlFor="link-tenant-check" className="text-sm text-slate-700 cursor-pointer">
                  Vincular a própria empresa como &quot;indicada&quot; pelo novo parceiro
                  <span className="block text-xs text-slate-500">
                    (útil para o cliente que quer começar a revender e receber comissão sobre os próximos indicados dele).
                  </span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 mt-0">
            <Button variant="outline" onClick={() => setPromoteTenant(null)}>Cancelar</Button>
            <Button onClick={confirmPromote} disabled={promoting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-promote">
              <Sparkles className="w-4 h-4 mr-1" />
              {promoting ? 'Promovendo...' : 'Tornar Parceiro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal criar/editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-form-modal">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Editar Parceiro' : 'Novo Parceiro'}</DialogTitle>
            <DialogDescription>
              {editingPartner
                ? 'Altere nome, telefone e % de comissão por plano. O email e ref_code não podem ser alterados.'
                : 'Cadastre um novo parceiro. Um usuário com role=partner será criado automaticamente e ele receberá um código de indicação único.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {formError && (
              <div className="p-3 bg-rose-50 border border-rose-300 rounded-lg text-sm text-rose-800 flex items-start gap-2" data-testid="form-error">
                <span className="font-semibold">⚠</span>
                <div>
                  <p className="font-medium">{formError}</p>
                  {formError.toLowerCase().includes('já cadastrado') && (
                    <p className="text-xs mt-1 text-rose-700">Use outro email ou edite o parceiro já existente na lista.</p>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-9999" data-testid="form-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} disabled={!!editingPartner} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
              </div>
              {!editingPartner && (
                <div>
                  <Label>Senha inicial *</Label>
                  <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="form-password" />
                </div>
              )}
            </div>

            <div>
              <Label className="mb-2 block">% de comissão por plano</Label>
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                {plans.map((plan) => (
                  <div key={plan.id} className="flex items-center gap-3">
                    <span className="flex-1 text-sm font-medium">{plan.name}</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      className="w-24"
                      value={form.commission_by_plan[plan.id] ?? 0}
                      onChange={(e) => setForm({
                        ...form,
                        commission_by_plan: {
                          ...form.commission_by_plan,
                          [plan.id]: parseFloat(e.target.value) || 0,
                        },
                      })}
                      data-testid={`form-commission-${plan.id}`}
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Ex: 10% de um plano de R$ 149,90 = R$ 14,99 de comissão TODO MÊS enquanto o cliente indicado pagar.
              </p>
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 mt-0">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-partner">
              {saving ? 'Salvando...' : editingPartner ? 'Salvar alterações' : 'Criar parceiro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GerenciarParceirosPage;
